import {
  Badge,
  Button,
  Flex,
  Group,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { IconCircleCheckFilled } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { LabelingTypeChip } from '@/components/annotations/LabelingTypes/LabelingTypeChip'
import { LabelGroup } from '@/components/labels/LabelGroup/LabelGroup'
import { RenderTableState } from '@/components/ui-shared/Tables/RenderTableState'
import { SortableLabel } from '@/components/ui-shared/Tables/SortableLabel/SortableLabel'
import { useColumnCount } from '@/components/ui-shared/Tables/useColumnCount'
import { MLModel, MLModelType } from '@/types/model'
import { SortConfig } from '@/utils/sorting'
import { ModelTypeChip } from '../ModelTypeChip/ModelTypeChip'
import { LibraryModelMoreInfo } from './LibraryModelMoreInfo'

export type TableSortConfig = SortConfig<'name'>

type LibraryModelListProps = {
  libraryModels: MLModel[]
  selectedModelId?: string
  sortConfig: TableSortConfig
  isLoading?: boolean
  hasError?: boolean
  onSort: (sortConfig: TableSortConfig) => void
  onTest: (libraryModel: MLModel) => void
  onRetrain: (libraryModel: MLModel) => void
  onDeploy: (libraryModel: MLModel) => void
  onReload?: () => void
}

export const LibraryModelList = ({
  libraryModels,
  selectedModelId,
  sortConfig,
  isLoading,
  hasError,
  onSort,
  onTest,
  onRetrain,
  onDeploy,
  onReload
}: LibraryModelListProps) => {
  const { tableRef, columnCount } = useColumnCount()

  return (
    <Table
      ref={tableRef}
      horizontalSpacing="xs"
      verticalSpacing="xs"
      highlightOnHoverColor="gray.0"
      withTableBorder
      highlightOnHover
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <SortableLabel
              label={<FormattedMessage id="models.library.list.columns.name" />}
              sortKey="name"
              sortConfig={sortConfig}
              onSort={onSort}
            />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="models.library.list.columns.type" />
          </Table.Th>

          <Table.Th w={360}>
            <FormattedMessage id="models.library.list.columns.labelingClasses" />
          </Table.Th>

          <Table.Th>&nbsp;</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {libraryModels.map((libraryModel) => (
          <Table.Tr
            key={libraryModel.id}
            style={
              libraryModel.id === selectedModelId
                ? {
                    outline: '1px solid var(--mantine-color-green-6)',
                    backgroundColor: 'var(--mantine-color-green-0)'
                  }
                : {}
            }
          >
            <Table.Td>
              <Group wrap="nowrap" gap="xs">
                {libraryModel.id === selectedModelId && (
                  <IconCircleCheckFilled
                    size={24}
                    color="var(--mantine-color-green-6)"
                  />
                )}

                <Text size="sm" fw={700}>
                  {libraryModel.name}
                </Text>

                <LibraryModelMoreInfo libraryModel={libraryModel} />

                {libraryModel.is_beta && (
                  <Badge color="brand-primary" size="xs">
                    <FormattedMessage id="general.badges.beta" />
                  </Badge>
                )}
              </Group>
            </Table.Td>

            <Table.Td>
              <Stack gap={4}>
                {libraryModel.labeling_type && (
                  <LabelingTypeChip labelingType={libraryModel.labeling_type} />
                )}

                {libraryModel.model_type === MLModelType.Secondary && (
                  <ModelTypeChip modelType={libraryModel.model_type} />
                )}
              </Stack>
            </Table.Td>

            <Table.Td>
              <Flex mih={48} direction="column" justify="center">
                <LabelGroup
                  labels={libraryModel.dataset_version?.labels || []}
                  maxLabelsShown={4}
                />
              </Flex>
            </Table.Td>

            <Table.Td>
              <Group justify="end">
                <Tooltip
                  label={
                    <FormattedMessage id="models.library.list.buttons.test.tooltip" />
                  }
                >
                  <Button
                    size="xs"
                    miw={80}
                    color="blue"
                    disabled={!libraryModel.can_be_tested}
                    onClick={() => onTest(libraryModel)}
                  >
                    <FormattedMessage id="models.library.list.buttons.test" />
                  </Button>
                </Tooltip>

                <Tooltip
                  label={
                    <FormattedMessage id="models.library.list.buttons.retrain.tooltip" />
                  }
                >
                  <Button
                    size="xs"
                    miw={80}
                    color="orange"
                    disabled={!libraryModel.can_be_trained}
                    onClick={() => onRetrain(libraryModel)}
                  >
                    <FormattedMessage id="models.library.list.buttons.retrain" />
                  </Button>
                </Tooltip>

                <Tooltip
                  label={
                    <FormattedMessage id="models.library.list.buttons.deploy.tooltip" />
                  }
                >
                  <Button
                    size="xs"
                    miw={80}
                    color="green"
                    disabled={!libraryModel.can_be_deployed}
                    onClick={() => onDeploy(libraryModel)}
                  >
                    <FormattedMessage id="models.library.list.buttons.deploy" />
                  </Button>
                </Tooltip>
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}

        <RenderTableState
          hasItems={libraryModels.length > 0}
          isLoading={isLoading}
          hasError={hasError}
          cols={columnCount}
          emptyMessage={<FormattedMessage id="models.library.list.empty" />}
          onReload={onReload}
        />
      </Table.Tbody>
    </Table>
  )
}
